// redux/slices/criticalDataSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TBD_FINDER_PAUSED } from '../../config/tbdFinder';
import { portalApi, fetchBackgroundData } from '../api/portalApi';
import { CRITICAL_RESOURCES, criticalDataArgs, criticalDataScope } from '../api/criticalDataContract';

/**
 * Helper that maps the raw /critical-data response payload to our internal
 * state shape.  Used by the RTK Query matchFulfilled matcher below.
 */
const mapCriticalDataPayload = (data) => ({
  dropdownValues: data.DropdownValues || [],
  hoverDescriptions: data.HoverDescriptions || [],
  globalVariables: data.GlobalVariables || [],
  tbdAudit: data.TBDAudit || [],
  holidays: data.Holidays || [],
  userPreferences: data.UserPreferences || [],
  iconData: data.IconData || [],
});

/**
 * Trigger a hard refresh of the critical-data cache.
 * A named resource refreshes only that list; no key explicitly refreshes the
 * aggregate once, without tag invalidation triggering a second request.
 *
 * Usage (backward-compatible replacement for the old refreshCriticalData thunk):
 *   dispatch(refreshCriticalData())
 */
export const refreshCriticalData = (apiKey) => (dispatch, getState) => {
  if (apiKey) return dispatch(refreshCriticalResource(apiKey));
  // Return the initiate call so callers can await it
  return dispatch(
    portalApi.endpoints.getCriticalData.initiate(criticalDataArgs(getState()), { forceRefetch: true, subscribe: false })
  );
};

/**
 * Backward-compatible alias kept so existing call-sites that do
 *   dispatch(fetchAllCriticalData({ forceRefresh, msalInstance, msalAccounts }))
 * continue to compile.  The MSAL parameters are no longer needed here because
 * user dropdowns are fetched independently in the dropdownSlice / App.js.
 *
 * When forceRefresh is true, request a fresh snapshot using the session key.
 */
export const fetchAllCriticalData =
  ({ forceRefresh = false } = {}) =>
  (dispatch, getState) => {
    // Always return the initiate promise so .unwrap() works in callers
    return dispatch(
      portalApi.endpoints.getCriticalData.initiate(criticalDataArgs(getState()), {
        forceRefetch: Boolean(forceRefresh),
        subscribe: false,
      })
    );
  };

// Shared by explicit post-save refreshes and realtime owners: concurrent reads
// share portalApi's transport rather than loading the entire aggregate again.
export const refreshCriticalResource = createAsyncThunk('criticalData/refreshResource',
  async (requestedKey, { dispatch, getState, signal }) => {
    const apiKey = requestedKey === 'tbdValues' ? 'tbdAudit' : requestedKey;
    if (TBD_FINDER_PAUSED && apiKey === 'tbdAudit') return getState().criticalData?.data?.tbdAudit || [];
    const resource = CRITICAL_RESOURCES[apiKey];
    if (!resource) throw new Error(`Unknown critical resource: ${requestedKey}`);
    const scope = criticalDataScope(getState());
    const url = getState().apiUrls?.urls?.[resource.apiKey];
    if (!url) throw new Error(`API is not configured: ${resource.apiKey}`);
    const payload = await dispatch(fetchBackgroundData({ url, signal }));
    const rows = Array.isArray(payload) ? payload : payload?.data;
    if (!Array.isArray(rows)) throw new Error('INVALID_CRITICAL_SNAPSHOT');
    if (scope !== criticalDataScope(getState())) throw new Error('OBSOLETE_CRITICAL_SNAPSHOT');
    dispatch(setCriticalData({ apiKey, data: rows }));
    return rows;
  });

const criticalDataSlice = createSlice({
  name: 'criticalData',
  initialState: {
    session: 0,
    scope: null,
    requestId: null,
    versions: {},
    requestVersions: {},
    // Store data by key (from unified API endpoint)
    data: {
      dropdownValues: null,
      hoverDescriptions: null,
      globalVariables: null,
      tbdAudit: null,
      holidays: null,
      userPreferences: null,
      iconData: null,
    },
    // Track last fetch time
    lastFetch: {
      all: null, // Single timestamp for unified fetch
    },
    // Global loading state
    loading: false,
    // Global error
    error: null,
    // Track initialization status
    initialized: false,
  },
  reducers: {
    /**
     * Clear all critical data (useful for logout)
     */
    clearCriticalData: (state) => {
      state.session += 1;
      state.scope = null;
      state.requestId = null;
      state.data = {
        dropdownValues: null,
        hoverDescriptions: null,
        globalVariables: null,
        tbdAudit: null,
        holidays: null,
        userPreferences: null,
        iconData: null,
      };
      state.lastFetch = {
        all: null,
      };
      state.loading = false;
      state.error = null;
      state.initialized = false;
    },
    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },
    /**
     * Manually set data for a specific key (useful for testing or pre-loading)
     */
    setCriticalData: (state, action) => {
      const { apiKey, data } = action.payload;
      if (state.data.hasOwnProperty(apiKey)) {
        state.data[apiKey] = data;
        state.versions[apiKey] = (state.versions[apiKey] || 0) + 1;
        state.error = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase('user/clearUserRoles', state => {
        state.session += 1;
        state.scope = null;
        state.requestId = null;
        state.initialized = false;
        state.loading = false;
        Object.keys(state.data).forEach(key => { state.data[key] = null; });
      })
      // ── RTK Query: getCriticalData ──────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.getCriticalData.matchPending,
        (state, action) => {
          const scope = JSON.stringify(action.meta.arg.originalArgs);
          if (state.scope !== scope) {
            Object.keys(state.data).forEach(key => { state.data[key] = null; });
            state.initialized = false;
          }
          state.scope = scope;
          state.requestId = action.meta.requestId;
          state.requestVersions = { ...state.versions };
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        portalApi.endpoints.getCriticalData.matchFulfilled,
        (state, action) => {
          if (state.requestId !== action.meta.requestId) return;
          const mapped = mapCriticalDataPayload(action.payload);
          // An aggregate started before a targeted/event read must not overwrite
          // the newer list when its slower response eventually arrives.
          Object.entries(mapped).forEach(([key, value]) => {
            if (state.versions[key] === state.requestVersions[key]) state.data[key] = value;
          });
          state.lastFetch.all = Date.now();
          state.loading = false;
          state.error = null;
          state.initialized = true;
          console.log('Critical data loaded successfully from API');
        }
      )
      .addMatcher(
        portalApi.endpoints.getCriticalData.matchRejected,
        (state, action) => {
          if (state.requestId !== action.meta.requestId) return;
          state.loading = false;
          state.error =
            action.error?.message || 'Failed to fetch critical data';
          console.error('getCriticalData failed:', action.error);
        }
      );
  },
});

// Selectors with safety checks for undefined state
export const selectCriticalData = (apiKey) => (state) => state.criticalData?.data?.[apiKey] || null;
export const selectCriticalDataLoading = (state) => state.criticalData?.loading || false;
export const selectCriticalDataError = (state) => state.criticalData?.error || null;
export const selectInitialized = (state) => state.criticalData?.initialized || false;
export const selectAllCriticalData = (state) => state.criticalData?.data || {
  dropdownValues: null,
  hoverDescriptions: null,
  globalVariables: null,
  tbdAudit: null,
  holidays: null,
  userPreferences: null,
  iconData: null,
};

// Backward compatibility - alias for selectCriticalDataLoading
export const selectGlobalLoading = (state) => state.criticalData?.loading || false;

// Convenience selectors for specific data types with safety checks
export const selectDropdownValues = (state) => state.criticalData?.data?.dropdownValues || null;
export const selectHoverDescriptions = (state) => state.criticalData?.data?.hoverDescriptions || null;
export const selectGlobalVariables = (state) => state.criticalData?.data?.globalVariables || null;
export const selectTbdAudit = (state) => state.criticalData?.data?.tbdAudit || null;
export const selectHolidays = (state) => state.criticalData?.data?.holidays || null;
export const selectUserPreferences = (state) => state.criticalData?.data?.userPreferences || null;
export const selectIconData = (state) => state.criticalData?.data?.iconData || null;

// Backward compatibility selectors (map old names to new structure)
export const selectTbdValues = (state) => state.criticalData?.data?.tbdAudit || null;

// Actions
export const { clearCriticalData, clearError, setCriticalData } = criticalDataSlice.actions;

export default criticalDataSlice.reducer;
